<template>
  <v-dialog
    :key="`report-detail-dialog-${reportIds.join('-') || 'default'}`"
    :value="dialog"
    :width="modalSpecial.REPORT_SUMMARY_WIDTH"
    :max-width="modalSpecial.REPORT_SUMMARY_MAX_WIDTH"
    content-class="examine-report-summary-dialog-content"
    scrollable
    v-bind="modalPolicy.P2_FORM"
    :data-testid="getTestId('report-detail-dialog')"
    @click:outside="closeDialog"
    @keydown.esc="closeDialog"
    @close="closeDialog"
  >
    <v-card
      v-if="!error"
      ref="reportCard"
      class="report-dialog"
      :data-testid="getTestId('report-detail-card')"
    >
      <table class="report-container-table">
        <tr>
          <td class="report-header-cell">
            <div class="report-header" :data-testid="getTestId('report-detail-header')">
              <div class="header-top">
                <div class="logo-section">
                  <SolvitLogoWhiteFull
                    class="company-logo"
                    :size="50"
                    :style="{ color: $vuetify.theme.currentTheme.text }"
                    aria-label="SOLVIT Logo"
                  />
                </div>
                <div class="title-section">
                  <h1 :data-testid="getTestId('report-detail-title')">
                    {{ $t('reportDetail.title') }}
                  </h1>
                </div>
                <div v-if="!loading && summary" class="document-info-section">
                  <table class="document-info-table">
                    <thead>
                      <tr>
                        <th class="info-header" colspan="2">
                          {{ $t('reportSummary.documentInfo') }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td class="info-label">{{ $t('reportSummary.inspectionOrg') }}</td>
                        <td class="info-value">
                          {{
                            summary?.inspectionCompanyName ||
                            $t('reportSummary.defaultInspectionOrg')
                          }}
                        </td>
                      </tr>
                      <tr>
                        <td class="info-label">{{ $t('reportSummary.docNumber') }}</td>
                        <td class="info-value">{{ generateDocumentNumber() }}</td>
                      </tr>
                      <tr>
                        <td class="info-label">{{ $t('reportSummary.createdDate') }}</td>
                        <td class="info-value">{{ formatDate(summary?.examineDate) }}</td>
                      </tr>
                    </tbody>
                  </table>
                  <div class="buttons-section">
                    <v-btn
                      v-show="!loading && summary"
                      outlined
                      class="solvit-crud-outline-btn solvit-crud-outline-btn--primary mr-2"
                      :loading="pdfLoading"
                      :data-testid="getTestId('detail-pdf-download-button')"
                      @click="downloadPdf"
                    >
                      <v-icon left> mdi-file-pdf-box </v-icon>
                      {{ $t('reportSummary.pdfDownload') }}
                    </v-btn>
                    <v-btn
                      outlined
                      class="solvit-crud-outline-btn"
                      :data-testid="getTestId('report-detail-close-button')"
                      @click="closeDialog"
                    >
                      <v-icon>mdi-close</v-icon>
                    </v-btn>
                  </div>
                </div>
              </div>
            </div>
          </td>
        </tr>

        <tr>
          <td class="report-content-cell">
            <!-- 1~4장: 요약 보고서와 동일한 공통 섹션 -->
            <ReportCommonSections :report-summary="summary" :loading="loading" />

            <!-- 5장: 검사 결함 상세 -->
            <div
              v-if="!loading"
              class="report-table page-break-before"
              :data-testid="getTestId('defect-detail-section')"
            >
              <h3 :data-testid="getTestId('defect-detail-title')">
                {{ $t('reportDetail.defectDetailTitle') }}
              </h3>

              <div
                v-if="!defects.length"
                class="no-defect-notice"
                :data-testid="getTestId('defect-detail-empty')"
              >
                {{ $t('reportDetail.noDefects') }}
              </div>

              <table
                v-for="(defect, idx) in defects"
                :key="defect.id || idx"
                :class="['defect-detail-table', { 'defect-detail-table--alt': idx % 2 === 1 }]"
                :data-testid="getTestId(`defect-detail-table-${defect.id || idx}`)"
              >
                <colgroup>
                  <col class="col-label" />
                  <col class="col-block" />
                  <col class="col-block" />
                  <col class="col-block" />
                  <col class="col-label" />
                  <col class="col-block" />
                  <col class="col-block" />
                  <col class="col-block" />
                </colgroup>
                <tbody>
                  <tr class="defect-meta-row">
                    <td class="cell-meta" colspan="8">
                      {{ $t('reportDetail.defectItem', { n: idx + 1 }) }}
                    </td>
                  </tr>
                  <!-- Row 1: PV Module / Defect ID / Defect Type -->
                  <tr>
                    <td class="cell-label">{{ $t('reportDetail.pvModuleNo') }}</td>
                    <td class="cell-value" colspan="2">{{ formatPosition(defect) }}</td>
                    <td class="cell-label">{{ $t('reportDetail.defectId') }}</td>
                    <td class="cell-value" colspan="2">
                      <span class="defect-no-text">{{ getValueOrDefault(defect.defectNo) }}</span>
                    </td>
                    <td class="cell-label">{{ $t('reportDetail.defectType') }}</td>
                    <td class="cell-value">
                      {{ localizedDefectTypeName(defect.defectTypeCode, defect.defectTypeName) }}
                    </td>
                  </tr>
                  <!-- Row 2: Max / Avg / Surround Module / ΔT -->
                  <tr>
                    <td class="cell-label">{{ $t('reportDetail.maxTemp') }}</td>
                    <td class="cell-value cell-temp">{{ formatTemp(defect.maxHeat) }}</td>
                    <td class="cell-label">{{ $t('reportDetail.avgTemp') }}</td>
                    <td class="cell-value cell-temp">{{ formatTemp(defect.averageHeat) }}</td>
                    <td class="cell-label">{{ $t('reportDetail.surroundModule') }}</td>
                    <td class="cell-value cell-temp">{{ getSurroundModuleDisplay(defect) }}</td>
                    <td class="cell-label">{{ $t('reportDetail.deltaT') }}</td>
                    <td class="cell-value cell-temp">{{ formatTempDelta(defect) }}</td>
                  </tr>
                  <!-- Row 3: 열화상 / RGB — 좌우 대칭 2블록 -->
                  <tr class="defect-image-row">
                    <td class="cell-label">{{ $t('reportDetail.thermalImage') }}</td>
                    <td class="cell-image" colspan="3">
                      <div class="defect-image-frame defect-image-frame--thermal">
                        <img
                          v-if="resolveCropUrl(defect.thermalCropUrl)"
                          :src="resolveCropUrl(defect.thermalCropUrl)"
                          class="defect-crop-img defect-crop-img--thermal"
                          :alt="$t('reportDetail.thermalImageAlt')"
                          @error="onCropImageError('thermal', defect)"
                        />
                        <span v-else class="no-image">{{ $t('reportDetail.noImage') }}</span>
                      </div>
                    </td>
                    <td class="cell-label">{{ $t('reportDetail.rgbImage') }}</td>
                    <td class="cell-image" colspan="3">
                      <div class="defect-image-frame defect-image-frame--rgb">
                        <img
                          v-if="resolveCropUrl(defect.rgbCropUrl)"
                          :src="resolveCropUrl(defect.rgbCropUrl)"
                          class="defect-crop-img defect-crop-img--rgb"
                          :alt="$t('reportDetail.rgbImageAlt')"
                          @error="onCropImageError('rgb', defect)"
                        />
                        <span v-else class="no-image">{{ $t('reportDetail.noImage') }}</span>
                      </div>
                    </td>
                  </tr>
                  <!-- Row 4: Defect Grade / 열화상 이미지명 -->
                  <tr>
                    <td class="cell-label">{{ $t('reportDetail.defectGrade') }}</td>
                    <td class="cell-value" colspan="2">
                      <span
                        v-if="defect.severityLabel"
                        class="severity-chip"
                        :style="severityStyle(defect)"
                      >
                        {{ severityText(defect) }}
                      </span>
                      <span v-else>{{ getDefectGrade(defect) }}</span>
                    </td>
                    <td class="cell-label">{{ $t('reportDetail.thermalImageName') }}</td>
                    <td class="cell-value cell-value--filename" colspan="3">
                      {{ getValueOrDefault(defect.thermalImageName) }}
                    </td>
                  </tr>
                  <tr>
                    <td class="cell-label">{{ $t('reportDetail.workInstruct') }}</td>
                    <td class="cell-value cell-value--left" colspan="7">
                      {{ getWorkInstruction(defect) }}
                    </td>
                  </tr>
                  <tr>
                    <td class="cell-label">{{ $t('reportDetail.memo') }}</td>
                    <td class="cell-value cell-value--left" colspan="7">
                      {{ getMemo(defect) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      </table>
    </v-card>
    <v-card v-else class="mx-auto" max-width="344">
      <v-img :src="require(`@/assets/image/solvit_logo.png`)" height="200px" />
      <CrudFormDialogHeader
        :kicker="$t('reportDetail.title')"
        :title="$t('reportSummary.errorTitle')"
      />
      <v-card-subtitle class="px-4">
        {{ $t('reportSummary.errorMessage') }}
      </v-card-subtitle>
    </v-card>
  </v-dialog>
</template>

<script>
  import { getTestId } from '@/utils/testUtils';
  import { usePdfDownload } from '@/utils/pdfUtils';
  import { resolveCdnImageUrl } from '@/utils/imagePolygonManager/imageLoader';
  import SolvitLogoWhiteFull from '@/components/brand/SolvitLogoWhiteFull.vue';
  import CrudFormDialogHeader from '@/components/common/CrudFormDialogHeader.vue';
  import ReportCommonSections from '@/components/dialog/ReportCommonSections.vue';
  import { MODAL_LAYOUT_SPECIAL, MODAL_CLOSE_POLICY } from '@/constants/modalLayout';
  import {
    formatReportDate,
    localizeDefectTypeLabel,
    parseReportDateParts
  } from '@/utils/reportLocaleUtils';

  export default {
    name: 'ReportDetailDialog',
    components: {
      SolvitLogoWhiteFull,
      CrudFormDialogHeader,
      ReportCommonSections
    },
    props: {
      dialog: {
        type: Boolean,
        default: false
      },
      reportName: {
        type: String,
        default: ''
      },
      reportIds: {
        type: Array,
        default: () => []
      },
      detailData: {
        type: Object,
        default: null
      }
    },
    data: () => ({
      modalSpecial: MODAL_LAYOUT_SPECIAL,
      modalPolicy: MODAL_CLOSE_POLICY,
      error: false,
      loading: true,
      pdfLoading: false,
      reportDetail: null
    }),
    computed: {
      summary() {
        return this.reportDetail?.summary || null;
      },
      defects() {
        return this.reportDetail?.defects || [];
      }
    },
    watch: {
      detailData: {
        handler(newData) {
          if (newData) {
            this.reportDetail = newData;
            this.loading = false;
            this.error = false;
          }
        },
        immediate: true
      }
    },
    methods: {
      getTestId(id) {
        return getTestId(id);
      },

      closeDialog() {
        this.$emit('close');
        this.$emit('update:dialog', false);
      },

      getValueOrDefault(value) {
        if (value === null || value === undefined || value === '') return '-';
        if (typeof value === 'string' && value.trim() === '') return '-';
        return value;
      },

      resolveCropUrl(url) {
        return resolveCdnImageUrl(url);
      },

      onCropImageError(kind, defect) {
        const rawUrl = kind === 'thermal' ? defect?.thermalCropUrl : defect?.rgbCropUrl;
        console.warn('[ReportDetail] crop image load failed', {
          kind,
          defectId: defect?.id,
          rawUrl,
          resolvedUrl: this.resolveCropUrl(rawUrl)
        });
      },

      /** 패널 위치: Array → String → Module */
      formatPosition(defect) {
        const normalize = (v) => {
          if (v === null || v === undefined) return '';
          const str = String(v).trim();
          return str === '-' ? '' : str;
        };
        const withPrefix = (prefix, value) => {
          const raw = normalize(value);
          if (!raw) return '';
          return raw.startsWith(prefix) ? raw : `${prefix}${raw}`;
        };

        const array = withPrefix('A', defect.arrayLabel);
        const string = withPrefix('S', defect.stringLabel);
        const module = withPrefix('M', defect.moduleLabel);
        const parts = [array, string, module].filter(Boolean);
        return parts.length ? parts.join(' - ') : '-';
      },

      /** 온도 값 포맷 (℃) */
      formatTemp(value) {
        if (value === null || value === undefined || Number.isNaN(Number(value))) return '-';
        return `${Number(value).toFixed(1)} ℃`;
      },

      formatTempDelta(defect) {
        const direct = defect?.deltaTemp ?? defect?.deltaT ?? defect?.temperatureGap;
        if (direct !== null && direct !== undefined && !Number.isNaN(Number(direct))) {
          return `${Number(direct).toFixed(1)} ℃`;
        }
        const max = Number(defect?.maxHeat);
        const min = Number(defect?.minHeat);
        if (Number.isNaN(max) || Number.isNaN(min)) return '-';
        return `${(max - min).toFixed(1)} ℃`;
      },

      getSurroundModule(defect) {
        const candidates = [
          defect?.surroundModule,
          defect?.surroundingModule,
          defect?.surroundModuleTemp,
          defect?.nearModuleTemp,
          defect?.nearModule,
          defect?.nearModuleLabel
        ];
        return this.firstValid(candidates);
      },

      getSurroundModuleDisplay(defect) {
        const raw = this.getSurroundModule(defect);
        if (raw === '-') return '-';
        if (!Number.isNaN(Number(raw))) return this.formatTemp(Number(raw));
        return raw;
      },

      getDefectGrade(defect) {
        const candidates = [defect?.defectGrade, defect?.severityGrade, defect?.grade];
        const found = this.firstValid(candidates);
        if (found !== '-') return found;
        return '-';
      },

      getWorkInstruction(defect) {
        const candidates = [
          defect?.workInstruction,
          defect?.actionPlan,
          defect?.repairGuide,
          defect?.recommendation
        ];
        return this.firstValid(candidates);
      },

      getMemo(defect) {
        const candidates = [defect?.memo, defect?.note, defect?.description, defect?.comment];
        return this.firstValid(candidates);
      },

      firstValid(candidates = []) {
        const found = candidates.find(
          (v) => v !== null && v !== undefined && String(v).trim() !== '' && String(v).trim() !== '-'
        );
        return found ? String(found) : '-';
      },

      severityText(defect) {
        const token = this.resolveSeverityToken(defect);
        return `${token.code} · ${token.label}`;
      },

      severityStyle(defect) {
        const token = this.resolveSeverityToken(defect);
        return {
          backgroundColor: token.background,
          borderColor: token.border,
          color: token.text
        };
      },

      resolveSeverityKey(labelRaw) {
        const normalized = String(labelRaw || '').trim().toLowerCase();
        const aliases = {
          trivial: ['trivial', 's0', '사소'],
          minor: ['minor', 's1', '경미'],
          moderate: ['moderate', 's2', '보통'],
          major: ['major', 's3', '중대', '심각'],
          critical: ['critical', 's4', '치명', '긴급']
        };
        for (const [key, list] of Object.entries(aliases)) {
          if (list.includes(normalized)) return key;
        }
        return null;
      },

      getSeverityStyleTokens(isDark) {
        return isDark
          ? {
              trivial: { background: '#0b4d2e', border: '#49d18f', text: '#edfff6' },
              minor: { background: '#0f5a37', border: '#62e2a3', text: '#effff7' },
              moderate: { background: '#685300', border: '#ffd86b', text: '#fff9e5' },
              major: { background: '#7c3a05', border: '#ffb86b', text: '#fff2e6' },
              critical: { background: '#7f1d1d', border: '#ff7f7f', text: '#ffeaea' }
            }
          : {
              trivial: { background: '#e8f8ee', border: '#59b87a', text: '#1f5132' },
              minor: { background: '#e2f6ea', border: '#409f66', text: '#1f4d33' },
              moderate: { background: '#fff6d8', border: '#c99800', text: '#684f00' },
              major: { background: '#ffe8d9', border: '#d57632', text: '#6e3500' },
              critical: { background: '#ffe2e2', border: '#cd4a4a', text: '#6b1c1c' }
            };
      },

      resolveSeverityToken(defect) {
        const labelRaw = String(defect?.severityLabel || '').trim();
        const severityKey = this.resolveSeverityKey(labelRaw);
        const isDark = Boolean(this.$vuetify?.theme?.dark);
        const styleTokens = this.getSeverityStyleTokens(isDark);
        const codeMap = {
          trivial: 'S0',
          minor: 'S1',
          moderate: 'S2',
          major: 'S3',
          critical: 'S4'
        };

        if (severityKey && styleTokens[severityKey]) {
          const style = styleTokens[severityKey];
          return {
            code: codeMap[severityKey],
            label: this.$t(`reportDetail.severityLevels.${severityKey}`),
            background: style.background,
            border: style.border,
            text: style.text
          };
        }

        const fallbackBg = defect?.severityColor || '#424242';
        return {
          code: 'S?',
          label: labelRaw || '-',
          background: fallbackBg,
          border: fallbackBg,
          text: this.getReadableTextColor(fallbackBg)
        };
      },

      getReadableTextColor(backgroundColor) {
        if (!backgroundColor) return 'var(--v-text-base)';
        const rgb = this.parseCssColor(backgroundColor);
        if (!rgb) return 'var(--v-text-base)';
        const [r, g, b] = rgb;
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        return luminance > 0.62 ? '#1a1a1a' : '#ffffff';
      },

      parseCssColor(color) {
        const value = String(color || '').trim();
        const hex = value.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
        if (hex) {
          const raw = hex[1];
          if (raw.length === 3) {
            return raw.split('').map(ch => parseInt(ch + ch, 16));
          }
          return [
            parseInt(raw.slice(0, 2), 16),
            parseInt(raw.slice(2, 4), 16),
            parseInt(raw.slice(4, 6), 16)
          ];
        }
        const rgb = value.match(/^rgba?\(\s*([0-9.]+)\s*,\s*([0-9.]+)\s*,\s*([0-9.]+)/i);
        if (rgb) {
          return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
        }
        return null;
      },

      calculateElementSize(element) {
        const rect = element.getBoundingClientRect();
        const width = Math.max(element.scrollWidth, element.offsetWidth, rect.width);
        return { width, height: 'auto' };
      },

      async downloadPdf() {
        try {
          this.pdfLoading = true;

          const reportElement = this.$refs.reportCard.$el;
          if (!reportElement) {
            throw new Error(this.$t('common.reportElementNotFound'));
          }

          const { width } = this.calculateElementSize(reportElement);
          const { downloadReport } = usePdfDownload();

          const fontOptions = {
            baseFontSize: '12px',
            baseFontWeight: 'normal',
            headerFontSize: '18px',
            headerFontWeight: 'bold',
            subHeaderFontSize: '14px',
            subHeaderFontWeight: 'bold',
            tableFontSize: '11px',
            tableFontWeight: 'normal',
            tableHeaderFontWeight: 'bold',
            lineHeight: '1.4',
            fontFamily: 'Arial, sans-serif',
            useCORS: true,
            scale: 2,
            width: width,
            backgroundColor: '#ffffff',
            displayHeaderFooter: true,
            headerTemplate: '<div></div>',
            footerTemplate: `
            <div style="font-size:10px; width:100%; text-align:right;
                        margin-right:20px; color:var(--v-secondary-base); font-family:Arial;">
              <span class="pageNumber"></span>
            </div>
          `,
            pageRanges: '1-',
            preferCSSPageSize: true,
            printBackground: true
          };

          await downloadReport(
            reportElement,
            this.generateDocumentNumber(),
            this.summary?.siteName,
            fontOptions
          );
        } catch (error) {
          console.error('상세 보고서 PDF 다운로드 실패:', error);
          this.$eventBus.$emit('show-alert', {
            type: 'error',
            message: this.$t('reportSummary.pdfDownloadError')
          });
        } finally {
          this.pdfLoading = false;
        }
      },

      generateDocumentNumber() {
        const parts = parseReportDateParts(this.summary?.examineDate);
        const now = new Date();
        const year = parts?.year ?? now.getFullYear();
        const month = String(parts?.month ?? now.getMonth() + 1).padStart(2, '0');
        const day = String(parts?.day ?? now.getDate()).padStart(2, '0');
        const currentReportId = this.reportIds.length > 0 ? this.reportIds[0] : 1;
        const sequenceNumber = String(currentReportId).padStart(6, '0');
        return `RPT-D-${year}-${month}-${day}-${sequenceNumber}`;
      },

      formatDate(dateString) {
        return formatReportDate(dateString, this.$i18n.locale);
      },

      localizedDefectTypeName(code, fallback) {
        return localizeDefectTypeLabel(
          key => this.$te(key),
          key => this.$t(key),
          code,
          fallback
        );
      }
    }
  };
</script>

<style scoped>
  .report-dialog {
    --report-table-border-strong: var(--v-text-base);
    --report-table-border-soft: var(--v-text-base);
    --report-label-text: var(--v-text-base);
    --report-label-bg: var(--v-background-base);
    --report-cell-bg: var(--v-surface-base);
    --report-image-border: var(--v-text-base);
    min-height: auto;
    max-height: none;
    background: transparent !important;
    padding: 30px;
  }

  .examine-report-summary-dialog-content {
    max-height: 90vh !important;
    overflow-y: auto !important;
  }

  @media print {
    .report-dialog {
      max-height: none !important;
      height: auto !important;
      overflow: visible !important;
    }
  }

  .v-card {
    background: transparent !important;
    box-shadow: none !important;
  }

  .report-container-table {
    width: 100%;
    border-collapse: collapse;
  }

  .report-container-table td {
    padding: 0;
    vertical-align: top;
  }

  .report-header {
    border-top: 3px solid var(--v-text-base);
    border-bottom: 3px solid var(--v-text-base);
    color: var(--v-text-base);
    font-size: 21px;
    padding: 10px 20px;
  }

  .header-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 15px;
  }

  .logo-section {
    flex: 0 0 auto;
  }

  .company-logo {
    display: block;
    height: 50px;
    width: 50px;
    flex-shrink: 0;
  }

  .title-section {
    flex: 1;
    text-align: center;
  }

  .title-section h1 {
    margin: 0;
    font-size: 24px;
    font-weight: bold;
    color: var(--v-text-base);
  }

  .document-info-section {
    flex: 0 0 auto;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 15px;
    min-width: 280px;
  }

  .document-info-table {
    border-collapse: collapse;
    border: 2px solid var(--v-text-base);
    background: var(--v-surface-base);
    font-family: 'Arial', sans-serif;
  }

  .document-info-table th.info-header {
    background-color: var(--v-surface-base);
    color: var(--v-text-base);
    font-weight: bold;
    font-size: 13px;
    padding: 8px 12px;
    border: 1px solid var(--v-text-base);
    text-align: center;
  }

  .document-info-table td.info-label {
    background-color: var(--v-surface-base);
    color: var(--v-text-base);
    font-weight: bold;
    font-size: 12px;
    padding: 6px 10px;
    border: 1px solid var(--v-text-base);
    text-align: left;
    width: 80px;
    white-space: nowrap;
  }

  .document-info-table td.info-value {
    background-color: var(--v-surface-base);
    color: var(--v-text-base);
    font-family: 'Courier New', monospace;
    font-size: 11px;
    padding: 6px 12px;
    border: 1px solid var(--v-text-base);
    text-align: left;
    min-width: 180px;
  }

  .buttons-section {
    flex: 0 0 auto;
  }

  /* 결함 상세 표 — 8컬럼(라벨1 + 블록3) × 2 반복 */
  .defect-detail-table {
    width: 96%;
    margin: 20px 20px;
    border: 2px solid var(--report-table-border-strong);
    border-collapse: collapse;
    table-layout: fixed;
    color: var(--v-text-base);
    font-family: 'Arial', sans-serif;
    page-break-inside: avoid;
  }

  .defect-detail-table--alt {
    border-color: var(--report-table-border-soft);
  }

  .defect-detail-table .col-label {
    width: 11%;
  }

  .defect-detail-table .col-block {
    width: 13%;
  }

  .defect-detail-table td {
    border: 1px solid var(--report-table-border-soft);
    padding: 8px 10px;
    vertical-align: middle;
    background: var(--report-cell-bg);
    font-size: 12px;
  }

  .defect-detail-table .defect-meta-row .cell-meta {
    font-weight: 700;
    font-size: 12px;
    letter-spacing: 0.2px;
    text-align: left;
    padding: 6px 10px;
    background: var(--report-label-bg);
    color: var(--report-label-text);
  }

  .defect-detail-table .cell-label {
    background: var(--report-label-bg);
    font-weight: 700;
    text-align: center;
    white-space: normal;
    line-height: 1.25;
    width: 110px;
    color: var(--report-label-text);
    font-size: 11px;
  }

  .defect-detail-table .cell-value {
    text-align: center;
  }

  .defect-detail-table .cell-value--left {
    text-align: left;
    padding-left: 12px;
  }

  .defect-no-text {
    display: inline-block;
    color: var(--v-text-base);
    font-family: 'Courier New', monospace;
    font-weight: 700;
    font-size: 12px;
    letter-spacing: 0.2px;
    white-space: nowrap;
  }

  .defect-detail-table .cell-temp {
    font-size: 13px;
    font-weight: 800;
    letter-spacing: 0.2px;
  }

  .defect-detail-table .defect-image-row .cell-label {
    vertical-align: middle;
  }

  .defect-detail-table .cell-image {
    padding: 8px;
    vertical-align: middle;
    background: var(--v-surface-base);
  }

  .defect-image-frame {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 200px;
    border: 1px solid var(--report-image-border);
    background: var(--report-cell-bg);
  }

  .defect-image-frame--thermal {
    max-width: 260px;
    margin: 0 auto;
  }

  .defect-image-frame--rgb {
    max-width: 340px;
    margin: 0 auto;
  }

  .defect-crop-img {
    display: block;
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  .defect-crop-img--thermal {
    max-width: 85%;
    max-height: 190px;
  }

  .defect-crop-img--rgb {
    max-width: 95%;
    max-height: 190px;
  }

  .defect-detail-table .cell-value--filename {
    text-align: left;
    padding-left: 12px;
    word-break: break-all;
  }

  .no-image {
    color: var(--v-secondary-base);
    font-size: 11px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 100%;
  }

  .severity-chip {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 26px;
    padding: 0 12px;
    border-radius: 999px;
    font-weight: 700;
    font-size: 12px;
    letter-spacing: 0.2px;
    border: 1px solid transparent;
  }

  .no-defect-notice {
    margin: 20px;
    padding: 20px;
    text-align: center;
    color: var(--v-secondary-base);
  }

  .report-table {
    margin-top: 30px;
    margin-bottom: 25px;
  }

  .report-table h3 {
    background-color: var(--v-surface-base);
    color: var(--v-text-base);
    padding: 12px;
    border-left: 4px solid var(--v-secondary-base);
    margin-bottom: 20px;
    border-radius: 4px;
    font-weight: bold;
    font-size: 16px;
    text-align: left;
  }

  .page-break-before {
    page-break-before: always;
    margin: 50px 0 0 0;
  }
</style>

<style lang="scss">
  .report-dialog.theme--light {
    --report-label-bg: #f1f3f5;
    --report-cell-bg: #fbfcfd;
    --report-table-border-strong: #49515f;
    --report-table-border-soft: #808a9a;
    --report-label-text: #2c3442;
    --report-image-border: #808a9a;
  }

  .report-dialog.theme--dark {
    --report-label-bg: #131722;
    --report-cell-bg: #1b1f2d;
  }

  .report-dialog.report-pdf-light-mode {
    --report-table-border-strong: #444444 !important;
    --report-table-border-soft: #444444 !important;
    --report-label-text: #1a1a1a !important;
    --report-label-bg: #f0f0f0 !important;
    --report-cell-bg: #ffffff !important;
    --report-image-border: #444444 !important;
  }

  @media print {
    .report-dialog {
      --v-text-base: #1a1a1a !important;
      --v-surface-base: #ffffff !important;
      --v-background-base: #ffffff !important;
      --v-secondary-base: #424242 !important;
      --v-primary-base: #1976d2 !important;
      --report-table-border-strong: #444444 !important;
      --report-table-border-soft: #444444 !important;
      --report-label-text: #1a1a1a !important;
      --report-label-bg: #f0f0f0 !important;
      --report-cell-bg: #ffffff !important;
      --report-image-border: #444444 !important;
      background: #ffffff !important;
      color: #1a1a1a !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }

    .report-dialog .report-table table th,
    .report-dialog .document-info-table th.info-header,
    .report-dialog .defect-detail-table .cell-label {
      background-color: var(--report-label-bg) !important;
      color: #1a1a1a !important;
    }

    .report-dialog img {
      background-color: transparent !important;
    }
  }
</style>
